// components/NavbarConfetti.js
import React, { useEffect, useRef, useState } from 'react';
import { useSeasonalTheme } from '../context/SeasonalThemeContext';
import './NavbarConfetti.css';

const PARTICLES_PER_BURST = 14;
const BURST_LIFETIME_MS = 900;

// Mounted once in Layout.js — listens for clicks anywhere on a navbar item
// (every one of them, docked or bubble-mode, shares the `.sb-item` class —
// see Navbar.js) and spawns a short-lived burst of the active theme's chosen
// emoji flying outward from the click point. A plain document click
// listener + a fixed-position portal-style overlay, rather than something
// baked into each nav item, so it works regardless of dock side/bubble mode
// without touching Navbar.js's own rendering.
export default function NavbarConfetti() {
  const { activeTheme } = useSeasonalTheme();
  const emoji = activeTheme?.navbar_confetti_emoji;
  const emojiRef = useRef(emoji);
  emojiRef.current = emoji;
  const [bursts, setBursts] = useState([]);
  const nextIdRef = useRef(0);

  useEffect(() => {
    function onClick(e) {
      if (!emojiRef.current) return;
      const item = e.target.closest('.sb-item');
      if (!item) return;
      const rect = item.getBoundingClientRect();
      const x = rect.left + rect.width / 2;
      const y = rect.top + rect.height / 2;
      const particles = Array.from({ length: PARTICLES_PER_BURST }, () => {
        const angle = Math.random() * Math.PI * 2;
        const distance = 40 + Math.random() * 60;
        return {
          dx: Math.cos(angle) * distance,
          dy: Math.sin(angle) * distance - 20, // slight upward bias, like real confetti
          rot: (Math.random() - 0.5) * 360,
          size: 14 + Math.random() * 10,
          delay: Math.random() * 0.08,
        };
      });
      const id = nextIdRef.current++;
      setBursts(prev => [...prev, { id, x, y, particles }]);
      setTimeout(() => setBursts(prev => prev.filter(b => b.id !== id)), BURST_LIFETIME_MS);
    }
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  if (!emoji || bursts.length === 0) return null;

  return (
    <div className="navbar-confetti-layer" aria-hidden="true">
      {bursts.map(b => (
        <div key={b.id} className="navbar-confetti-burst" style={{ left: b.x, top: b.y }}>
          {b.particles.map((p, i) => (
            <span
              key={i}
              className="navbar-confetti-particle"
              style={{
                fontSize: p.size,
                animationDelay: `${p.delay}s`,
                '--dx': `${p.dx}px`,
                '--dy': `${p.dy}px`,
                '--rot': `${p.rot}deg`,
              }}
            >
              {emoji}
            </span>
          ))}
        </div>
      ))}
    </div>
  );
}
